import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { HeagleRenderer, IntervalOption, WebSocketUrlOption } from './types';
import webSocketService from './services/websocketService';
import IntervalSelector from './components/IntervalSelector';
import WebSocketSelector from './components/WebSocketSelector';
import TotalConnections from './components/TotalConnections';
import RendererTable from './components/RendererTable';
import RendererFilter from './components/RendererFilter';
import ConnectionChart from './components/ConnectionChart';
import './App.css';
import './utils/websocketDebug';

interface ChartData {
  timestamp: number;
  client_count: number;
}

// 차트에 표시할 시간 범위 (1시간)
const CHART_TIME_RANGE_MS = 3600000; // 1시간

function App() {
  const [renderers, setRenderers] = useState<HeagleRenderer[]>([]);
  const [chartData, setChartData] = useState<Map<number, ChartData[]>>(new Map());
  const [selectedInterval, setSelectedInterval] = useState<IntervalOption>('10초');
  const [selectedWebSocketUrl, setSelectedWebSocketUrl] = useState<WebSocketUrlOption>('wss://ssr-dev.conworth.net/webrtc/server');
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedRendererIds, setSelectedRendererIds] = useState<number[]>([]);
  const [selectedRendererGroups, setSelectedRendererGroups] = useState<string[]>([]);

  const intervalMap: Record<IntervalOption, number> = useMemo(() => ({
    '1초': 1000,
    '5초': 5000,
    '10초': 10000,
    '30초': 30000,
    '1분': 60000,
    '5분': 300000,
  }), []);

  // 브라우저 타이틀 설정
  useEffect(() => {
    document.title = 'Renderer Monitor';
  }, []);

  const handleDataReceived = useCallback((data: HeagleRenderer[]) => {
    setRenderers(data);
    
    setChartData(prevData => {
      const now = Date.now();
      const chartRangeStart = now - CHART_TIME_RANGE_MS;
      const newChartData = new Map(prevData);
      const currentRendererIds = new Set(data.map(r => r.renderer_id));
      
      // 각 renderer별로 차트 데이터 업데이트
      data.forEach(renderer => {
        const existingData = newChartData.get(renderer.renderer_id) || [];
        
        // 새 데이터 추가
        const updatedData = [...existingData, {
          timestamp: now,
          client_count: renderer.client_count
        }]
          // 차트 범위를 넘어서는 오래된 데이터 삭제
          .filter(d => d.timestamp >= chartRangeStart)
          // 시간순으로 정렬
          .sort((a, b) => a.timestamp - b.timestamp);
        
        newChartData.set(renderer.renderer_id, updatedData);
      });
      
      // 더 이상 존재하지 않는 renderer의 오래된 데이터도 정리
      newChartData.forEach((rendererData, rendererId) => {
        if (!currentRendererIds.has(rendererId)) {
          // 차트 범위를 넘어서는 데이터만 삭제 (renderer가 사라져도 최근 데이터는 유지)
          const filteredData = rendererData
            .filter(d => d.timestamp >= chartRangeStart)
            .sort((a, b) => a.timestamp - b.timestamp);
          
          if (filteredData.length > 0) {
            newChartData.set(rendererId, filteredData);
          } else {
            // 범위 내 데이터가 없으면 완전히 삭제
            newChartData.delete(rendererId);
          }
        }
      });
      
      return newChartData;
    });
    
    setError(null);
  }, []);

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  const handleConnectionStatus = useCallback((connected: boolean) => {
    setIsConnected(connected);
    if (connected) {
      setError(null);
    }
  }, []);

  useEffect(() => {
    webSocketService.connect(handleDataReceived, handleError, handleConnectionStatus, selectedWebSocketUrl);

    return () => {
      webSocketService.disconnect();
      setIsConnected(false);
    };
  }, [handleDataReceived, handleError, handleConnectionStatus, selectedWebSocketUrl]);

  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      webSocketService.requestData();
    }, intervalMap[selectedInterval]);

    return () => clearInterval(interval);
  }, [selectedInterval, isConnected, intervalMap]);

  const handleIntervalChange = (interval: IntervalOption) => {
    setSelectedInterval(interval);
  };

  const handleWebSocketUrlChange = (url: WebSocketUrlOption) => {
    setSelectedWebSocketUrl(url);
    setRenderers([]);
    setChartData(new Map());
    webSocketService.disconnect();
    webSocketService.connect(handleDataReceived, handleError, handleConnectionStatus, url);
  };

  const handleManualReconnect = () => {
    webSocketService.manualReconnect();
  };

  // 필터링된 렌더러 목록
  const filteredRenderers = useMemo(() => {
    // 필터가 없으면 전체 표시
    if (selectedRendererIds.length === 0 && selectedRendererGroups.length === 0) {
      return renderers;
    }

    // OR 관계: Renderer ID 필터 또는 Renderer Group 필터 중 하나라도 만족하면 표시
    const filtered = renderers.filter(renderer => {
      // Renderer ID 필터 체크
      const matchesId = selectedRendererIds.length > 0 && 
        selectedRendererIds.includes(renderer.renderer_id);
      
      // Renderer Group 필터 체크
      const rendererGroup = renderer.renderer_group || '';
      const matchesGroup = selectedRendererGroups.length > 0 && 
        selectedRendererGroups.includes(rendererGroup);
      
      // OR 관계: 둘 중 하나라도 만족하면 true
      return matchesId || matchesGroup;
    });
    
    // 선택된 ID 중 실제 데이터에 없는 것도 플레이스홀더로 추가
    if (selectedRendererIds.length > 0) {
      const existingIds = new Set(filtered.map(r => r.renderer_id));
      const missingIds = selectedRendererIds.filter(id => !existingIds.has(id));
      
      const placeholderRenderers: HeagleRenderer[] = missingIds.map(id => ({
        renderer_id: id,
        renderer_group: '',
        renderer_name: '(접속 대기 중)',
        renderer_port: 0,
        max_conn: 0,
        client_count: 0
      }));
      
      return [...filtered, ...placeholderRenderers];
    }
    
    return filtered;
  }, [renderers, selectedRendererIds, selectedRendererGroups]);

  return (
    <div className="App">
      <header className="App-header">
        <h1>Renderer Monitor</h1>
        <div className="connection-status">
          <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '● 연결됨' : '● 연결 끊김'}
          </span>
          {!isConnected && (
            <button 
              className="reconnect-button"
              onClick={handleManualReconnect}
            >
              재연결
            </button>
          )}
        </div>
        <div className="selectors-container">
          <WebSocketSelector 
            selectedUrl={selectedWebSocketUrl}
            onUrlChange={handleWebSocketUrlChange}
          />
          <IntervalSelector 
            selectedInterval={selectedInterval}
            onIntervalChange={handleIntervalChange}
          />
        </div>
      </header>

      <main className="App-main">
        {error && (
          <div className="error-message">
            <p>오류: {error}</p>
          </div>
        )}

        <div className="status-section">
          <TotalConnections renderers={renderers} />
        </div>

        <div className="table-section">
          <RendererFilter
            renderers={renderers}
            selectedRendererIds={selectedRendererIds}
            selectedRendererGroups={selectedRendererGroups}
            onRendererIdsChange={setSelectedRendererIds}
            onRendererGroupsChange={setSelectedRendererGroups}
          />
          <RendererTable renderers={filteredRenderers} />
        </div>

        <div className="chart-section">
          <ConnectionChart 
            renderersData={renderers.map(renderer => ({
              rendererId: renderer.renderer_id,
              rendererName: renderer.renderer_name,
              rendererGroup: renderer.renderer_group || '',
              data: chartData.get(renderer.renderer_id) || []
            }))}
            selectedRendererIds={selectedRendererIds}
            selectedRendererGroups={selectedRendererGroups}
          />
        </div>
      </main>
    </div>
  );
}

export default App;